import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { searchCompanies } from '../services/api';
import '../styles/header.css';

const APP_BASE_URL = process.env.REACT_APP_APP_URL || 'https://risqly.net';

function Header() {
  const { loginWithRedirect, logout, isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Handle search input with debouncing
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const results = await searchCompanies(searchQuery);
        setSuggestions(results || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search failed:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle suggestion click
  const handleSuggestionClick = (company) => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchFocused(false);
    navigate(`/company/${encodeURIComponent(company.normalized_name)}`);
  };

  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Do nothing - user must click a suggestion to navigate
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!searchFocused) return;
      const target = e.target;

      if (target && target.closest && (target.closest('.search-form') || target.closest('.search-button'))) {
        return;
      }

      if (searchRef.current && target && searchRef.current.contains(target)) {
        return;
      }

      setShowSuggestions(false);
      setSearchFocused(false);
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [searchFocused]);

  const isOnDashboard = location.pathname.startsWith('/dashboard');

  const openDashboard = async () => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return;
    }

    try {
      await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });
      navigate('/dashboard');
    } catch (err) {
      console.warn('Dashboard access requires fresh login:', err);
      loginWithRedirect();
    }
  };

  return (
    <header className="header">
      {searchFocused && <div className="search-backdrop" onClick={() => setSearchFocused(false)} />}
      <div className={`header-container ${isOnDashboard ? 'on-dashboard' : ''}`}>
        <div className="header-left">
          {isAuthenticated && !isOnDashboard && (
            <button
              className="btn btn-secondary dashboard-button"
              onClick={openDashboard}
              disabled={isLoading}
            >
              <span className="material-symbols-outlined">dashboard</span>
            </button>
          )}
          <button 
            className="search-button"
            onClick={() => {
              setSearchFocused(true);
              // Focus the input after a brief delay to ensure it's visible
            setTimeout(() => inputRef.current?.focus(), 50);
          }}
          aria-label="Search"
        >
          <span className="material-symbols-outlined">search</span>
        </button>
        </div>
        <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <span className="material-symbols-outlined logo-icon">security</span>
          <span className="logo-text">RISQLY</span>
        </div>
        <nav className="header-nav">
          <button
            className="nav-link header-companies-link"
            onClick={() => navigate('/companies')}
            type="button"
          >
            Companies
          </button>
          <form
            className={`search-form ${searchFocused ? 'search-focused' : ''}`}
            onSubmit={handleSearchSubmit}
            ref={searchRef}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="search-input-wrapper">
              <span className="material-symbols-outlined search-icon">search</span>
              <input
                ref={inputRef}
                type="text"
                className="search-input"
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  setSearchFocused(true);
                  if (searchQuery.trim().length >= 2) setShowSuggestions(true);
                }}
              />
              {loading && <span className="search-loading">...</span>}
            </div>

            {showSuggestions && searchQuery.trim().length >= 2 && (
              <div className="search-suggestions">
                {suggestions.map((company) => (
                  <div
                    key={company.id}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(company)}
                  >
                    <span className="material-symbols-outlined suggestion-icon">graph_5</span>
                    <div className="suggestion-content">
                      <div className="suggestion-name">{company.normalized_name} ({company.domain})</div>
                      <div className="suggestion-score">Score: {company.final_score ?? 'N/A'} / 100</div>
                    </div>
                  </div>
                ))}
                {!loading && (
                  <div 
                    className="suggestion-item"
                    onClick={() => {
                      setSearchQuery('');
                      setSuggestions([]);
                      setShowSuggestions(false);
                      setSearchFocused(false);
                      navigate(`/company/${encodeURIComponent(searchQuery)}`);
                    }}
                  >
                    <span className="material-symbols-outlined suggestion-icon">graph_5</span>
                    <div className="suggestion-content">
                      <div className="suggestion-name">{searchQuery.charAt(0).toUpperCase() + searchQuery.slice(1)}</div>
                      <div className="suggestion-score">Score: 0 / 100</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </form>
          {isAuthenticated ? (
            <div className="auth-group">
              <span className="user-email">{user?.email}</span>
              {!location.pathname.startsWith('/dashboard') && (
                <button
                  className="btn btn-secondary desktop-dashboard-btn"
                  onClick={openDashboard}
                  disabled={isLoading}
                >
                  Dashboard
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() =>
                  logout({
                    logoutParams: {
                      returnTo: APP_BASE_URL
                    }
                  })
                }
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => loginWithRedirect()}
            >
              <span className="login-full-text">Log In / Register</span>
              <span className="login-mobile-text">Log In</span>
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Header;
